import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./FeedbackSurvey.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

function StarRating({ value, onChange, label, leftLabel, rightLabel }) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="fs-question">
      <p className="fs-question-label">{label} <span className="fs-required">*</span></p>
      <div className="fs-star-row">
        <span className="fs-pole-label">{leftLabel}</span>
        <div className="fs-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              className={`fs-star ${n <= (hovered || value) ? "fs-star--active" : ""}`}
              onMouseEnter={() => setHovered(n)}
              onMouseLeave={() => setHovered(0)}
              onClick={() => onChange(n)}
              aria-label={`${n} star`}
            >
              ★
            </button>
          ))}
        </div>
        <span className="fs-pole-label">{rightLabel}</span>
      </div>
    </div>
  );
}

function RadioGroup({ label, name, options, value, onChange, required = false }) {
  return (
    <div className="fs-question">
      <p className="fs-question-label">{label}{required && <span className="fs-required"> *</span>}</p>
      <div className="fs-radio-group">
        {options.map((opt) => (
          <label key={opt.value} className={`fs-radio-option ${value === opt.value ? "fs-radio-option--selected" : ""}`}>
            <input
              type="radio"
              name={name}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
            />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function FeedbackSurvey() {
  const { token } = useParams();

  const [surveyInfo, setSurveyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [overallRating, setOverallRating] = useState(0);
  const [funRating, setFunRating] = useState(0);
  const [pacing, setPacing] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [enjoyedMost, setEnjoyedMost] = useState("");
  const [improveNext, setImproveNext] = useState("");
  const [returnLikelihood, setReturnLikelihood] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/feedback/survey/${token}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.detail || "Survey not found or has expired.");
          setLoading(false);
          return;
        }
        const data = await res.json();
        setSurveyInfo(data);
        if (data.already_submitted) setSubmitted(true);
      } catch {
        setError("Could not load the survey. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError("");

    if (!overallRating) return setSubmitError("Please rate your overall experience.");
    if (!funRating) return setSubmitError("Please rate how fun the event was.");
    if (!pacing) return setSubmitError("Please select how the pacing felt.");
    if (!enjoyedMost.trim()) return setSubmitError("Please share what you enjoyed most.");
    if (!improveNext.trim()) return setSubmitError("Please share what we could improve.");
    if (!returnLikelihood) return setSubmitError("Please tell us if you'd come back.");

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/feedback/survey/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          overall_rating: overallRating,
          fun_rating: funRating,
          pacing,
          difficulty: difficulty || null,
          enjoyed_most: enjoyedMost,
          improve_next: improveNext,
          return_likelihood: returnLikelihood,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setSubmitError(data.detail || "Something went wrong. Please try again.");
        return;
      }

      setSubmitted(true);
    } catch {
      setSubmitError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="fs-page">
        <div className="fs-container">
          <div className="fs-card fs-card--center">
            <div className="fs-spinner" />
            <p className="fs-meta">Loading your survey…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fs-page">
        <div className="fs-container">
          <div className="fs-card fs-card--center">
            <div className="fs-icon fs-icon--error">✕</div>
            <h2 className="fs-heading">Link Expired</h2>
            <p className="fs-meta">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="fs-page">
        <div className="fs-container">
          <div className="fs-card fs-card--center">
            <div className="fs-icon fs-icon--success">✓</div>
            <h2 className="fs-heading">Thank You!</h2>
            <p className="fs-subheading">
              Your feedback on <strong>{surveyInfo?.event_name}</strong> has been received.
            </p>
            <p className="fs-meta">We'll use it to make the next event even better. See you there! 🎉</p>
          </div>
        </div>
      </div>
    );
  }

  const eventName = surveyInfo?.event_name || "the event";

  return (
    <div className="fs-page">
      <div className="fs-container">
        <div className="fs-card">

          {/* Header */}
          <div className="fs-header">
            <p className="fs-eyebrow">Block Club</p>
            <h1 className="fs-title">{eventName} Feedback</h1>
            <p className="fs-subtitle">
              Thanks so much for coming! This takes about 1–2 minutes and helps us make the next event even better.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="fs-form">

            <StarRating
              label={`How would you rate your overall experience at ${eventName}?`}
              value={overallRating}
              onChange={setOverallRating}
              leftLabel="Not great"
              rightLabel="Amazing"
            />

            <StarRating
              label="How fun was the event itself?"
              value={funRating}
              onChange={setFunRating}
              leftLabel="Not very fun"
              rightLabel="Extremely fun"
            />

            <RadioGroup
              label="How did the pacing of the event feel?"
              name="pacing"
              required
              value={pacing}
              onChange={setPacing}
              options={[
                { value: "too_slow", label: "Too slow" },
                { value: "just_right", label: "Just right" },
                { value: "too_fast", label: "Too fast" },
              ]}
            />

            <RadioGroup
              label="How did the difficulty feel?"
              name="difficulty"
              value={difficulty}
              onChange={setDifficulty}
              options={[
                { value: "too_easy", label: "Too easy" },
                { value: "just_right", label: "Just right" },
                { value: "too_hard", label: "Too hard" },
              ]}
            />

            <div className="fs-question">
              <label className="fs-question-label" htmlFor="enjoyedMost">
                What did you enjoy most about the event? <span className="fs-required">*</span>
              </label>
              <textarea
                id="enjoyedMost"
                className="fs-textarea"
                rows={3}
                placeholder="The atmosphere, the mystery plot, the people…"
                value={enjoyedMost}
                onChange={(e) => setEnjoyedMost(e.target.value)}
              />
            </div>

            <div className="fs-question">
              <label className="fs-question-label" htmlFor="improveNext">
                What could we improve for next time? <span className="fs-required">*</span>
              </label>
              <textarea
                id="improveNext"
                className="fs-textarea"
                rows={3}
                placeholder="Lighting, pacing, venue layout…"
                value={improveNext}
                onChange={(e) => setImproveNext(e.target.value)}
              />
            </div>

            <RadioGroup
              label={`Would you come to another ${eventName} event?`}
              name="returnLikelihood"
              required
              value={returnLikelihood}
              onChange={setReturnLikelihood}
              options={[
                { value: "definitely", label: "Definitely" },
                { value: "probably", label: "Probably" },
                { value: "maybe", label: "Maybe" },
                { value: "probably_not", label: "Probably not" },
              ]}
            />

            {submitError && (
              <p className="fs-error">{submitError}</p>
            )}

            <button
              type="submit"
              className="fs-submit-btn"
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit Feedback"}
            </button>

          </form>
        </div>
      </div>
    </div>
  );
}
